/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { forkJoin, Subscription } from 'rxjs';

import { AbstractObject } from '@src/app/model/abstract-object';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { SrClass } from '@src/app/model/sr-class';
import { SrTopic } from '@src/app/model/sr-topic';
import { CloseRequestData, RequestService } from '@src/app/modules/crm/request.service';
import { SrClassService } from '@src/app/modules/crm/sr-class.service';
import { SrTopicService } from '@src/app/modules/crm/sr-topic.service';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { FieldSet, FieldSetComponent } from '@src/app/shared/form/field-set/field-set.component';
import { FormDurationComponent } from '@src/app/shared/form/form-duration/form-duration.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { MessageService } from '@src/app/shared/message.service';

export interface ResolveWorkOrderData {
    sr: ServiceRequest;
}

/**
 * The ways a work order can end. Each one fixes both halves of the close at once - the close status
 * that says what happened, and the status the work order lands on - so there is nothing left to ask
 * about the money afterwards.
 */
const CHARGE_COMING = 'chargeComing';
const NO_CHARGE = 'noCharge';
const CANCELLED = 'cancelled';

/**
 * Closing a work order, which is not closing a case. Nobody is waiting to be told the work is done -
 * we ordered it - so no comment and no mail; the note becomes the closing action's description.
 *
 * Resolved means the supplier says the work is complete; accepted means the charge for it - the
 * invoice, or an accrual - has been entered and linked to it, which the purchase page does. A work
 * order only stays resolved while a charge is still expected, so the single outcome question here
 * covers both what happened and where it lands.
 */
@Component({
    selector: 'app-resolve-work-order',
    imports: [MatDialogModule, FieldSetComponent, MatButtonModule, DialogBannerComponent],
    templateUrl: './resolve-work-order.component.html',
    styleUrl: './resolve-work-order.component.scss'
})
export class ResolveWorkOrderComponent implements OnDestroy {
    dialogRef = inject(MatDialogRef<ResolveWorkOrderComponent>);
    data = inject<ResolveWorkOrderData>(MAT_DIALOG_DATA);
    srSvc = inject(RequestService);
    msgSvc = inject(MessageService);
    topicSvc = inject(SrTopicService);
    classSvc = inject(SrClassService);
    screenSvc = inject(IsNarrowService);

    sr: ServiceRequest = this.data.sr;
    title = $localize`Close Work Order`;
    saveText = $localize`Close Work Order`;

    isPhone = false;
    ready = false;
    comment = '';
    subscriptions: Subscription[] = [];

    private classes: SrClass[] = [];

    fieldSet = new FieldSet({ formLayout: [{ cells: [{ width: '33%' }, { width: '66%' }] }] });

    requestIdField = FieldMaker.idHolder('requestId');

    closeOutcomeField = FormPicklistComponent.make($localize`Close Status`, 'closeOutcome', null,
        { items: this.closeOutcomeItems(), allowSelectNone: false,
          refreshes: [this.closeOutcomeRefresh.bind(this)] },
        { sendServer: false }
    );

    topicClass = ServiceRequest.makeTopicAndClassFields(() => this.classes);

    // Recorded on the closing action, not mailed anywhere - see the help text below.
    closeTextField = FormRichTextComponent.make('What was done?', 'closeText', { formColumn: 2 });

    closeWorkField = FormDurationComponent.make('Time to complete', 'closeWorkTime', {});

    fields = [
        this.requestIdField,
        this.closeOutcomeField,
        this.closeTextField,
        this.closeWorkField,
        this.topicClass.topicField.override({ formColumn: 1 }),
        this.topicClass.classField.override({ formColumn: 1 }),
    ];

    constructor() {
        this.subscriptions.push(this.screenSvc.detectVeryNarrow().subscribe(r => { this.isPhone = r }));
        this.fieldSet.setFields(this.fields);
        this.requestIdField.setValue({ requestId: this.sr.id } as unknown as AbstractObject);
        this.loadRefData();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.subscriptions = [];
    }

    private closeOutcomeItems() {
        const items = [
            {
                id: CHARGE_COMING, name: $localize`Work Complete - Charge Coming`,
                tip: $localize`The work is done and the charge for it is still to come. The work
                    order is resolved; entering the invoice, or an accrual, against it accepts it.`
            },
            {
                id: NO_CHARGE, name: $localize`Work Complete - No Charge`,
                tip: $localize`The work is done and nothing will ever be charged for it, so the
                    work order is accepted now - there is no invoice coming to accept it later.`
            },
            {
                id: CANCELLED, name: $localize`Cancelled`,
                tip: $localize`The work was called off and never carried out, so there is nothing
                    to charge and nothing to wait for. The work order is accepted and closed.`
            },
        ];
        return items as AbstractObject[];
    }

    /** Both halves of the close, so no outcome can leave a work order in a state that contradicts it. */
    private outcomeOf(id: string): { srCloseStatus: string, finalStatus: string } {
        switch (id) {
            case NO_CHARGE:
                return { srCloseStatus: ServiceRequest.srComplete.id as string,
                         finalStatus: ServiceRequest.srAccepted.id as string };
            case CANCELLED:
                return { srCloseStatus: ServiceRequest.srCancelled.id as string,
                         finalStatus: ServiceRequest.srAccepted.id as string };
            default:
                return { srCloseStatus: ServiceRequest.srComplete.id as string,
                         finalStatus: ServiceRequest.srResolved.id as string };
        }
    }

    private loadRefData() {
        forkJoin({
            topics: this.topicSvc.get<SrTopic>(false),
            classes: this.classSvc.get<SrClass>(false),
        }).subscribe(r => {
            this.classes = r.classes;
            this.topicClass.topicField.setPicklistItems(r.topics);
            this.topicClass.narrowClasses(this.sr.srTopic);
            this.topicClass.topicField.setValue({ srTopic: this.sr.srTopic } as unknown as AbstractObject);
            this.topicClass.classField.setValue({ srClass: this.sr.srClass } as unknown as AbstractObject);
            this.closeOutcomeField.setValue(
                { closeOutcome: CHARGE_COMING } as unknown as AbstractObject);
            // An hour rather than the case default of fifteen minutes: engaging a supplier, chasing
            // them and settling the paperwork is a different order of work from answering a query.
            this.closeWorkField.setValue({ closeWorkTime: 60 } as unknown as AbstractObject);
            this.closeOutcomeRefresh(this.closeOutcomeField.control.value as AbstractObject);
            this.ready = true;
        });
    }

    closeOutcomeRefresh(closeOutcome: AbstractObject) {
        const chosen = this.closeOutcomeItems().find(i => i.id === (closeOutcome?.id ?? closeOutcome));
        this.comment = (chosen?.['tip'] as string) ?? '';
    }

    highlightErrors() {
        this.fields.forEach(f => {
            if (!f.control?.valid) {
                f.control.markAsTouched();
            }
        });
    }

    /**
     * Only closes on a request that actually moved - closeRequest hands back what the server has, so
     * a status that did not change means the close did not happen. Leaving the dialog open lets the
     * user see the error and try again.
     */
    closeRequest() {
        const value = this.fieldSet.getFormValue() as unknown as CloseRequestData;
        const outcome = this.outcomeOf(this.chosenOutcome());

        // teamId is not a field on the dialog, and closeRequest addresses the write with it.
        this.srSvc.closeRequest({ ...value, ...outcome, teamId: this.sr.teamId })
            .subscribe(response => {
                if (response?.srStatus === outcome.finalStatus) {
                    this.dialogRef.close(response);
                } else {
                    this.msgSvc.showErrorMessage(
                        $localize`${this.title} did not complete - the work order is unchanged`);
                }
            }, error => this.msgSvc.showError(error, this.title));
    }

    private chosenOutcome(): string {
        const ctl = this.closeOutcomeField.control?.value as AbstractObject | string;
        return (typeof ctl === 'string' ? ctl : ctl?.id) as string;
    }
}
